import React, { useEffect, useRef, useState } from "react";

import type {
  Entity,
  FieldConfig,
  FormConfig,
  FormDataItem,
} from "../../types";
import { convertDDMMYYYYToYYYYMMDD, validateField } from "./formUtils";

// ── Types ───────────────────────────────────────────────────────────────

interface UseFormRendererParams {
  /** Whether the hosting modal is currently open */
  isOpen: boolean;
  /** Normalized form configuration (fields must already be arrays) */
  formConfig: FormConfig;
  /** Entity list — one form per entity */
  entities: Entity[];
  /**
   * Extracts the raw data object from an entity.
   * - EditTravellers: `(entity) => entity.data`
   * - AddNominee:     `(entity) => entity.data.nomineeDetails?.[0] ?? {}`
   */
  extractEntityData: (entity: Entity) => Record<string, unknown>;
}

export interface UseFormRendererReturn {
  formData: FormDataItem[];
  setFormData: React.Dispatch<React.SetStateAction<FormDataItem[]>>;
  errors: Record<number, Record<string, string>>;
  setErrors: React.Dispatch<
    React.SetStateAction<Record<number, Record<string, string>>>
  >;
  initialDataRef: React.MutableRefObject<FormDataItem[]>;
  getFieldRef: (
    entityIndex: number,
    fieldKey: string
  ) => React.RefObject<HTMLInputElement>;
  getInitialValue: (entityIndex: number, fieldKey: string) => string;
  handleFieldChange: (
    entityIndex: number,
    fieldKey: string,
    value: string
  ) => void;
  handleMaskedFocus: (entityIndex: number, fieldKey: string) => void;
  handleMaskedBlur: (
    entityIndex: number,
    fieldKey: string,
    currentValue: string,
    field: FieldConfig
  ) => void;
  handleDynamicFieldChange: (
    entityIndex: number,
    triggerId: string,
    fieldKey: string,
    value: string
  ) => void;
  toggleDynamicField: (entityIndex: number, triggerId: string) => void;
  validateAllFields: (entityFilter?: (index: number) => boolean) => boolean;
  hasFormDataChanged: (entityFilter?: (index: number) => boolean) => boolean;
  scrollToFirstError: (
    errorMap: Record<number, Record<string, string>>
  ) => void;
}

// ── Hook ────────────────────────────────────────────────────────────────

export const useFormRenderer = ({
  isOpen,
  formConfig,
  entities,
  extractEntityData,
}: UseFormRendererParams): UseFormRendererReturn => {
  const [formData, setFormData] = useState<FormDataItem[]>([]);
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>(
    {}
  );

  const initialDataRef = useRef<FormDataItem[]>([]);
  const prevIsOpenRef = useRef(false);
  const fieldRefs = useRef<
    Record<number, Record<string, React.RefObject<HTMLInputElement>>>
  >({});

  // ── Refs ────────────────────────────────────────────────────────────

  const getFieldRef = (
    entityIndex: number,
    fieldKey: string
  ): React.RefObject<HTMLInputElement> => {
    if (!fieldRefs.current[entityIndex]) {
      fieldRefs.current[entityIndex] = {};
    }
    if (!fieldRefs.current[entityIndex][fieldKey]) {
      fieldRefs.current[entityIndex][fieldKey] = React.createRef();
    }
    return fieldRefs.current[entityIndex][fieldKey];
  };

  // ── Initialization ──────────────────────────────────────────────────

  useEffect(() => {
    if (isOpen && entities.length > 0) {
      prevIsOpenRef.current = true;

      const initialData: FormDataItem[] = entities.map((entity) => {
        const raw = extractEntityData(entity);
        const data: FormDataItem = {};

        formConfig.fields.forEach((field) => {
          const fieldKey = field.name;
          let value = (raw[fieldKey] as string | undefined) || "";
          if (field.type === "DATE" && value) {
            value = convertDDMMYYYYToYYYYMMDD(String(value));
          } else if (field.type === "RADIO" || field.type === "SELECT") {
            value = String(value || "").trim();
          } else {
            value = String(value || "");
          }
          data[fieldKey] = value;
        });

        const dynamicFieldsState: Record<
          string,
          { visible: boolean; [key: string]: any }
        > = {};
        formConfig.dynamicFields?.forEach((df) => {
          const firstField = df.fields[0];
          const firstFieldKey = firstField?.name;
          const hasExistingValue = firstFieldKey
            ? Boolean(raw[firstFieldKey])
            : false;
          const shouldBeVisible = firstField?.isVisible || hasExistingValue;

          dynamicFieldsState[df.triggerId] = { visible: shouldBeVisible };
          df.fields.forEach((field) => {
            dynamicFieldsState[df.triggerId][field.name] =
              (raw[field.name] as string | undefined) || "";
          });
        });
        data.dynamicFields = dynamicFieldsState;

        return data;
      });

      setFormData(initialData);
      setErrors({});
      initialDataRef.current = JSON.parse(JSON.stringify(initialData));
    } else if (!isOpen) {
      prevIsOpenRef.current = false;
    }
  }, [isOpen, entities, formConfig]);

  // ── Masked field helpers ────────────────────────────────────────────

  const getInitialValue = (entityIndex: number, fieldKey: string): string =>
    String(initialDataRef.current[entityIndex]?.[fieldKey] ?? "");

  const handleMaskedFocus = (entityIndex: number, fieldKey: string) => {
    setFormData((prev) => {
      const updated = [...prev];
      updated[entityIndex] = { ...updated[entityIndex], [fieldKey]: "" };
      return updated;
    });
  };

  const handleMaskedBlur = (
    entityIndex: number,
    fieldKey: string,
    currentValue: string,
    field: FieldConfig
  ) => {
    if (!currentValue.trim()) {
      const maskedValue = getInitialValue(entityIndex, fieldKey);
      setFormData((prev) => {
        const updated = [...prev];
        updated[entityIndex] = {
          ...updated[entityIndex],
          [fieldKey]: maskedValue,
        };
        return updated;
      });
      setErrors((prev) => ({
        ...prev,
        [entityIndex]: { ...prev[entityIndex], [fieldKey]: "" },
      }));
      return;
    }
    const error = validateField(currentValue, field);
    setErrors((prev) => ({
      ...prev,
      [entityIndex]: { ...prev[entityIndex], [fieldKey]: error },
    }));
  };

  // ── Field change handlers ──────────────────────────────────────────

  const handleFieldChange = (
    entityIndex: number,
    fieldKey: string,
    value: string
  ) => {
    setFormData((prev) => {
      const updated = [...prev];
      updated[entityIndex] = { ...updated[entityIndex], [fieldKey]: value };
      return updated;
    });

    const field = formConfig.fields.find((f) => f.name === fieldKey);
    if (field) {
      const error = validateField(value, field);
      setErrors((prev) => ({
        ...prev,
        [entityIndex]: { ...prev[entityIndex], [fieldKey]: error },
      }));
    }
  };

  const handleDynamicFieldChange = (
    entityIndex: number,
    triggerId: string,
    fieldKey: string,
    value: string
  ) => {
    setFormData((prev) => {
      const updated = [...prev];
      const entity = { ...updated[entityIndex] };
      const dynamicFields = { ...entity.dynamicFields };
      dynamicFields[triggerId] = {
        ...(dynamicFields[triggerId] || { visible: false }),
        [fieldKey]: value,
      };
      entity.dynamicFields = dynamicFields;
      updated[entityIndex] = entity;
      return updated;
    });

    const dynamicField = formConfig.dynamicFields?.find(
      (df) => df.triggerId === triggerId
    );
    const field = dynamicField?.fields.find((f) => f.name === fieldKey);
    if (field) {
      const error = validateField(value, field);
      setErrors((prev) => ({
        ...prev,
        [entityIndex]: {
          ...prev[entityIndex],
          [`${triggerId}_${fieldKey}`]: error,
        },
      }));
    }
  };

  const toggleDynamicField = (entityIndex: number, triggerId: string) => {
    const dynamicFieldConfig = formConfig.dynamicFields?.find(
      (df) => df.triggerId === triggerId
    );

    setFormData((prev) => {
      const updated = [...prev];
      const entity = { ...updated[entityIndex] };
      const dynamicFields = { ...entity.dynamicFields };
      const currentVisibility = dynamicFields[triggerId]?.visible || false;
      const newVisibility = !currentVisibility;

      const triggerState: { visible: boolean; [key: string]: unknown } = {
        ...(dynamicFields[triggerId] || {}),
        visible: newVisibility,
      };

      if (!newVisibility && dynamicFieldConfig) {
        dynamicFieldConfig.fields.forEach((field) => {
          triggerState[field.name] = "";
        });
      }

      if (newVisibility && !dynamicFields[triggerId] && dynamicFieldConfig) {
        dynamicFieldConfig.fields.forEach((field) => {
          triggerState[field.name] = "";
        });
      }

      dynamicFields[triggerId] = triggerState;
      entity.dynamicFields = dynamicFields;
      updated[entityIndex] = entity;
      return updated;
    });

    if (dynamicFieldConfig) {
      setErrors((prev) => {
        const updatedErrors = { ...prev };
        if (updatedErrors[entityIndex]) {
          const entityErrors = { ...updatedErrors[entityIndex] };
          dynamicFieldConfig.fields.forEach((field) => {
            delete entityErrors[`${triggerId}_${field.name}`];
          });
          if (Object.keys(entityErrors).length === 0) {
            delete updatedErrors[entityIndex];
          } else {
            updatedErrors[entityIndex] = entityErrors;
          }
        }
        return updatedErrors;
      });
    }
  };

  // ── Scroll to error ────────────────────────────────────────────────

  const scrollToFirstError = (
    errorMap: Record<number, Record<string, string>>
  ): void => {
    const indices = Object.keys(errorMap)
      .map(Number)
      .sort((a, b) => a - b);

    for (const entityIndex of indices) {
      const entityErrors = errorMap[entityIndex];
      if (!entityErrors) continue;

      const firstErrorKey = Object.keys(entityErrors).find(
        (key) => entityErrors[key]
      );
      if (!firstErrorKey) continue;

      const fieldRef = fieldRefs.current[entityIndex]?.[firstErrorKey];
      if (fieldRef?.current) {
        fieldRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        setTimeout(() => fieldRef.current?.focus(), 300);
        return;
      }
    }
  };

  // ── Validation ─────────────────────────────────────────────────────

  const validateAllFields = (
    entityFilter?: (index: number) => boolean
  ): boolean => {
    const newErrors: Record<number, Record<string, string>> = {};
    let isValid = true;

    formData.forEach((entityData, entityIndex) => {
      if (entityFilter && !entityFilter(entityIndex)) return;

      formConfig.fields.forEach((field) => {
        const fieldKey = field.name;
        if (!field.isVisible || !field.isEnabled) return;

        const value = String(entityData[fieldKey] || "");

        // Skip validation for masked fields that haven't been changed
        if (field.isMasked && value === getInitialValue(entityIndex, fieldKey))
          return;

        const error = validateField(value, field);
        if (error) {
          isValid = false;
          if (!newErrors[entityIndex]) newErrors[entityIndex] = {};
          newErrors[entityIndex][fieldKey] = error;
        }
      });

      formConfig.dynamicFields?.forEach((df) => {
        const isVisible =
          entityData.dynamicFields?.[df.triggerId]?.visible || false;
        if (!isVisible) return;

        df.fields.forEach((field) => {
          if (!field.isEnabled) return;
          const fieldKey = field.name;
          const value = String(
            (entityData.dynamicFields?.[df.triggerId]?.[fieldKey] as
              | string
              | undefined) || ""
          );
          const error = validateField(value, field);
          if (error) {
            isValid = false;
            if (!newErrors[entityIndex]) newErrors[entityIndex] = {};
            newErrors[entityIndex][`${df.triggerId}_${fieldKey}`] = error;
          }
        });
      });
    });

    setErrors(newErrors);
    if (!isValid) {
      setTimeout(() => scrollToFirstError(newErrors), 100);
    }
    return isValid;
  };

  // ── Change detection ───────────────────────────────────────────────

  const hasFormDataChanged = (
    entityFilter?: (index: number) => boolean
  ): boolean => {
    if (
      initialDataRef.current.length === 0 ||
      formData.length === 0 ||
      initialDataRef.current.length !== formData.length
    ) {
      return initialDataRef.current.length !== formData.length;
    }

    for (let i = 0; i < formData.length; i++) {
      if (entityFilter && !entityFilter(i)) continue;

      const current = formData[i];
      const initial = initialDataRef.current[i];

      for (const field of formConfig.fields) {
        const fieldKey = field.name;
        if (
          String(current[fieldKey] || "").trim() !==
          String(initial[fieldKey] || "").trim()
        ) {
          return true;
        }
      }

      if (formConfig.dynamicFields) {
        for (const df of formConfig.dynamicFields) {
          const currentDynamic = current.dynamicFields?.[df.triggerId];
          const initialDynamic = initial.dynamicFields?.[df.triggerId];

          if (currentDynamic?.visible !== initialDynamic?.visible) return true;

          if (currentDynamic?.visible) {
            for (const field of df.fields) {
              const fieldKey = field.name;
              if (
                String(currentDynamic[fieldKey] || "").trim() !==
                String(initialDynamic?.[fieldKey] || "").trim()
              ) {
                return true;
              }
            }
          }
        }
      }
    }

    return false;
  };

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    initialDataRef,
    getFieldRef,
    getInitialValue,
    handleFieldChange,
    handleMaskedFocus,
    handleMaskedBlur,
    handleDynamicFieldChange,
    toggleDynamicField,
    validateAllFields,
    hasFormDataChanged,
    scrollToFirstError,
  };
};
